import { css } from "aphrodite/no-important";
import {
  clampDate,
  getMonthYear,
  getStartOfToday,
} from "components/helpers/filters";
import { Moment } from "moment";
import React, { useEffect, useState } from "react";
import { useThemedStyleSheet } from "util/hooks/useThemedStyleSheet";
import { Calendar } from "./Calendar";
import { NavButton } from "./NavButton";
import { backIconStyles, forwardIconStyles } from "./navButtonStyles";
import { styles } from "./styles";

interface DatePickerProps {
  handleSelectionChange: (date: Moment) => void;
  isAutoFocusDisabled?: boolean;
  isSelectingEndDate?: boolean;
  maxDate?: Moment;
  minDate?: Moment;
  name: string;
  selectedEndDate?: Moment | null;
  selectedStartDate: Moment | null;
  shouldHideAutoFocus?: boolean;
}

const controlInstructions = "Cursor keys can navigate dates.";

export const DatePicker = ({
  handleSelectionChange,
  isAutoFocusDisabled,
  isSelectingEndDate,
  maxDate,
  minDate,
  name,
  selectedEndDate,
  selectedStartDate,
  shouldHideAutoFocus = true,
}: DatePickerProps) => {
  const [focusedDate, setFocusedDate] = useState(
    (isSelectingEndDate ? selectedEndDate : selectedStartDate) ||
      getStartOfToday()
  );
  const [instructions, setInstructions] = useState("");
  const [shouldAutoFocus, setShouldAutoFocus] = useState(true);
  const [isAutoFocusHidden, setIsAutoFocusHidden] = useState(true);
  const themedStyles = useThemedStyleSheet(styles);

  const monthYear = getMonthYear(focusedDate);

  const headingLabelId = `${name}-heading`;

  const changeFocus = (date: Moment) => {
    const clampedDate = clampDate(date, minDate, maxDate);
    setFocusedDate(clampedDate);
  };

  const handleBlur = () => {
    setInstructions("");
  };

  const handleClickNextMonth = () => {
    const nextMonth = focusedDate.clone();
    nextMonth.add(1, "month");
    changeFocus(nextMonth);
    setShouldAutoFocus(false);
    setIsAutoFocusHidden(shouldHideAutoFocus);
  };

  const handleClickPreviousMonth = () => {
    const previousMonth = focusedDate.clone();
    previousMonth.subtract(1, "month");
    changeFocus(previousMonth);
    setShouldAutoFocus(false);
    setIsAutoFocusHidden(shouldHideAutoFocus);
  };

  const handleFocus = () => {
    setInstructions(controlInstructions);
  };

  const handleFocusChange = (date: Moment) => {
    changeFocus(date);
    setShouldAutoFocus(true);
    setIsAutoFocusHidden(false);
  };

  useEffect(() => {
    setFocusedDate(
      (isSelectingEndDate ? selectedEndDate : selectedStartDate) ||
        getStartOfToday()
    );
    setIsAutoFocusHidden(shouldHideAutoFocus);
  }, [isSelectingEndDate]);

  return (
    <div className={css(themedStyles.datePicker)}>
      <div className={css(themedStyles.header)}>
        <NavButton
          handleClick={handleClickPreviousMonth}
          iconStyle={backIconStyles}
          label="Previous month"
        />
        <h2
          aria-live="polite"
          className={css(themedStyles.heading)}
          id={headingLabelId}
        >
          {monthYear}
        </h2>
        <NavButton
          handleClick={handleClickNextMonth}
          iconStyle={forwardIconStyles}
          label="Next month"
        />
      </div>
      <Calendar
        focusedDate={focusedDate}
        handleBlur={handleBlur}
        handleFocus={handleFocus}
        handleFocusChange={handleFocusChange}
        handleSelectionChange={handleSelectionChange}
        labelId={headingLabelId}
        minDate={minDate}
        maxDate={maxDate}
        selectedEndDate={selectedEndDate}
        selectedStartDate={selectedStartDate}
        shouldAutoFocus={shouldAutoFocus && !isAutoFocusDisabled}
        shouldHideAutoFocus={isAutoFocusHidden}
      />
      <div aria-live="polite" className="sr-only">
        {instructions}
      </div>
    </div>
  );
};
